import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '../../../components/ui/Dialog';
import { Button } from '../../../components/ui/Button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../components/ui/Select';
import { useClients } from '../../../core/hooks/useClients';
import { useOrganizations } from '../../../core/hooks/useOrganizations';
import type { User } from '../../../core/models/User';
import type { ClientWithOrg } from '../../../core/models/Client';
import { Building2 } from 'lucide-react';
import { LoadingState } from '../../../components/shared/LoadingState';

interface ClientAccessDialogProps {
  open: boolean;
  onClose: () => void;
  user: User;
  onSave: (clientAccess: Record<string, { role: string }>) => Promise<void>;
}

type ClientAccessRole = 'ADMIN' | 'USER' | 'VIEWER' | null;

export const ClientAccessDialog: React.FC<ClientAccessDialogProps> = ({
  open,
  onClose,
  user,
  onSave,
}) => {
  const { clients, loading: clientsLoading } = useClients(user.organizationId || null);
  const { organizations } = useOrganizations();
  const [clientAccessMap, setClientAccessMap] = useState<Record<string, ClientAccessRole>>({});
  const [isSaving, setIsSaving] = useState(false);
  const isSuperAdmin = user.role === 'SUPER_ADMIN';
  const isProjectAdmin = user.role === 'PROJECT_ADMIN';

  // Initialize client access map from user's current clientAccess
  useEffect(() => {
    if (!open) return; // Only initialize when dialog is open

    const initialMap: Record<string, ClientAccessRole> = {};

    // Initialize with existing client access
    if (user.clientAccess) {
      Object.entries(user.clientAccess).forEach(([clientId, access]) => {
        initialMap[clientId] = access.role as ClientAccessRole;
      });
    }

    // Initialize all clients (those without access will be null/undefined, which shows as "No Access")
    clients.forEach((client) => {
      if (!(client.id in initialMap)) {
        initialMap[client.id] = null;
      }
    });

    setClientAccessMap(initialMap);
  }, [user, clients, open]);

  // Get organization name helper
  const getOrganizationName = (orgId: string): string => {
    const org = organizations.find((o) => o.id === orgId);
    return org?.name || orgId;
  };

  // Get client organization name
  const getClientOrganizationName = (client: ClientWithOrg): string => {
    if (client.organizationName) {
      return client.organizationName;
    }
    if (client.organizationId) {
      return getOrganizationName(client.organizationId);
    }
    return '';
  };

  const handleRoleChange = (clientId: string, role: ClientAccessRole) => {
    if (isProjectAdmin && role) {
      // Project admins can have access to exactly one project.
      setClientAccessMap({ [clientId]: role });
      return;
    }

    setClientAccessMap((prev) => ({
      ...prev,
      [clientId]: role,
    }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Only include clients with assigned roles (not null)
      const clientAccess: Record<string, { role: string }> = {};
      Object.entries(clientAccessMap).forEach(([clientId, role]) => {
        if (role) {
          clientAccess[clientId] = { role };
        }
      });

      if (isProjectAdmin) {
        const allowedProjectIds = Object.keys(clientAccess).slice(0, 1);
        const constrainedAccess: Record<string, { role: string }> = {};
        allowedProjectIds.forEach((projectId) => {
          constrainedAccess[projectId] = clientAccess[projectId];
        });
        await onSave(constrainedAccess);
        onClose();
        return;
      }

      await onSave(clientAccess);
      onClose();
    } catch (err: any) {
      console.error('Error saving client access:', err);
      alert(err?.message || 'Failed to save project access. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  // Enrich clients with organization names
  const clientsWithOrg: ClientWithOrg[] = clients.map((client) => ({
    ...client,
    organizationName: getClientOrganizationName(client as ClientWithOrg),
  }));

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Manage Project Access</DialogTitle>
          <DialogDescription>
            {user.firstName} {user.lastName}
          </DialogDescription>
        </DialogHeader>

        {clientsLoading ? (
          <LoadingState message="Loading projects..." />
        ) : isSuperAdmin ? (
          <div className="text-sm text-gray-600 py-4">
            Super Admin users automatically have access to all client accounts and projects.
          </div>
        ) : (
          <div className="flex-1 overflow-hidden flex flex-col">
            {/* Header */}
            <div className="grid grid-cols-[1fr_140px] gap-4 px-1 py-2 border-b border-gray-200 mb-2">
              <div className="font-medium text-sm text-gray-600">Project Information</div>
              <div className="font-medium text-sm text-gray-600 text-center">Access Level</div>
            </div>

            {/* Client List */}
            <div className="flex-1 overflow-y-auto space-y-3 pr-2">
              {clientsWithOrg.length === 0 ? (
                <div className="text-center text-gray-500 py-8">
                  No projects found for this client.
                </div>
              ) : (
                clientsWithOrg.map((client) => (
                  <div
                    key={client.id}
                    className="grid grid-cols-[1fr_140px] gap-4 items-center py-3 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-base text-gray-900 truncate">
                        {client.name}
                      </p>
                      {client.organizationName && (
                        <p className="text-xs text-gray-500 flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {client.organizationName}
                        </p>
                      )}
                    </div>
                    <div>
                      <Select
                        value={clientAccessMap[client.id] || 'none'}
                        onValueChange={(value) =>
                          handleRoleChange(client.id, value === 'none' ? null : (value as ClientAccessRole))
                        }
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">No Access</SelectItem>
                          <SelectItem value="VIEWER">Viewer</SelectItem>
                          <SelectItem value="USER">User</SelectItem>
                          <SelectItem value="ADMIN">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button type="button" onClick={handleSave} loading={isSaving}>
            Save Changes
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
